import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { FileText, Download, Loader2, User, Briefcase, Mail, Phone, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPack() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/vault/shared/${shareId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Share not found'))
      .finally(() => setLoading(false));
  }, [shareId]);

  const download = (docId, filename) => {
    const url = `${API}/vault/shared/${shareId}/document/${docId}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <h1 className="font-heading text-3xl mb-2">SHARE NOT AVAILABLE</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const p = data.profile || {};
  const expiryDate = data.expires_at ? new Date(data.expires_at) : null;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-2xl" data-testid="public-pack-page">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">SHARED APPLICATION PACK</div>
        <h1 className="font-heading text-3xl md:text-4xl">{data.label}</h1>
        {expiryDate && (
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" /> Expires {expiryDate.toLocaleDateString()}
          </div>
        )}
      </div>

      {p && Object.values(p).some(Boolean) && (
        <section className="border border-border mb-6" data-testid="pack-profile">
          <div className="px-5 py-3 border-b border-border bg-secondary/30">
            <h2 className="font-heading text-sm uppercase tracking-widest">APPLICANT</h2>
          </div>
          <div className="p-5 space-y-2 text-sm">
            <Row icon={<User className="w-4 h-4" />} label="Name" value={p.name} />
            <Row icon={<Mail className="w-4 h-4" />} label="Email" value={p.email} />
            <Row icon={<Phone className="w-4 h-4" />} label="Phone" value={p.phone} />
            <Row icon={<Briefcase className="w-4 h-4" />} label="Employer" value={p.current_employer && `${p.current_employer} — ${p.job_title || 'N/A'}`} />
            {p.years_experience && <Row icon={<Briefcase className="w-4 h-4" />} label="Experience" value={`${p.years_experience} years`} />}
          </div>
        </section>
      )}

      <section className="border border-border" data-testid="pack-documents">
        <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
          <h2 className="font-heading text-sm uppercase tracking-widest">DOCUMENTS · {data.documents.length}</h2>
        </div>
        {data.documents.length === 0 ? (
          <div className="p-8 text-center text-xs uppercase tracking-widest text-muted-foreground">No documents attached</div>
        ) : (
          data.documents.map((doc) => (
            <div key={doc.document_id} className="px-5 py-3 border-b border-border/60 last:border-b-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-sm truncate">{doc.original_filename}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                    {doc.doc_type?.replace(/_/g, ' ')} · {formatSize(doc.size)}
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(doc.document_id, doc.original_filename)} className="uppercase text-xs tracking-wider" data-testid={`dl-${doc.document_id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> View
              </Button>
            </div>
          ))
        )}
      </section>

      <div className="mt-10 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
        POWERED BY <a href="/" className="hover:text-foreground">LIFE ADMIN AUTOFILL</a>
      </div>
    </div>
  );
}

function Row({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-mono text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

function formatSize(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1024 / 1024).toFixed(1) + ' MB';
}
